<template>
  <v-row density="compact">
    <div class="text-subtitle-1">
      <strong>Only available for dates after October 1, 2021</strong>
      Aggregates daily options for a stock. The short-term tenor includes options that expire in the
      next 30 days. The long-term tenor includes options that expire over 90-days from the current
      date.
    </div>

    <v-col cols="12" sm="12">
      <v-select
        :model-value="rule.config.target"
        :items="mappings"
        item-value="id"
        item-title="description"
        label="Field to evaluate"
        hint=""
        hide-details
        density="compact"
        variant="outlined"
        @update:model-value="updateConfig('target', $event)"
      >
      </v-select>
    </v-col>
    <v-col cols="12" sm="12">
      <v-select
        :model-value="rule.config.action"
        :items="actions"
        item-value="id"
        item-title="description"
        label="Action"
        hint=""
        density="compact"
        hide-details
        variant="outlined"
        @update:model-value="updateConfig('action', $event)"
      />
    </v-col>

    <v-col cols="12" sm="6">
      <v-text-field
        :model-value="rule.config.threshold"
        density="compact"
        hide-details
        variant="outlined"
        label="Number to compare to"
        @update:model-value="updateConfig('threshold', $event)"
      />
    </v-col>
    <v-col cols="12">
      <div class="text-subtitle-1">
        For the daily score, 0 = extremely bearish, 50 = neutral, 100 = extremely bullish. The score
        uses percentile ranks across "+ "all available stock options, so the score is relative to
        the stock universe as a whole. Inputs to the score include:
        <ul>
          <li>Call/Put Ratio</li>
          <li>Net delta exposure</li>
          <li>call_vega - put_vega</li>
          <li>Put Implied Volatility - Call Implied Volatility</li>
          <li>Change in Call Open Interest - Change in Put Open Interest</li>
          <li>OTM Call activity vs OTM Put activity</li>
          <li>Short-dated theta demand bias</li>
        </ul>
      </div>
    </v-col>
  </v-row>
</template>

<script>
// Modules
import { mapFields } from '@/store/helpers'

export default {
  components: {},
  props: {
    rule: { type: Object, required: true }
  },
  emits: ['update:rule'],
  data() {
    return {
      actions: [
        { id: 1, description: 'Crossed Above' },
        { id: 2, description: 'Crossed Below' },
        { id: 3, description: 'Is Above' },
        { id: 4, description: 'Is Below' }
      ],
      mappings: [
        {
          id: 32,
          description: 'Daily Score Short-Term',
          field: 'score_st',
          help:
            '0 = extremely bearish, 50 = neutral, 100 = extremely bullish.  The score uses percentile ranks across ' +
            'all available stock options, so the score is relative to the stock universe as a whole.'
        },
        {
          id: 33,
          description: 'Daily Score Long-Term',
          field: 'score_lt',
          help:
            '0 = extremely bearish, 50 = neutral, 100 = extremely bullish.  The score uses percentile ranks across ' +
            'all available stock options, so the score is relative to the stock universe as a whole.'
        },
        {
          id: 73,
          description: 'Daily Score Medium-Term',
          field: 'score_mt',
          help:
            '0 = extremely bearish, 50 = neutral, 100 = extremely bullish.  The score uses percentile ranks across ' +
            'all available stock options, so the score is relative to the stock universe as a whole.'
        },
       // { id: 74, description: 'Flatness Score', field: 'flatness_score' },
        { id: 75, description: 'Bullish Intensity Short-Term', field: 'bullish_intensity_st' },
        { id: 76, description: 'Bullish Accumulation Short-Term', field: 'bullish_accumulation_st' },
        { id: 77, description: 'Bullish Accumulation Flag Short-Term', field: 'bullish_accum_flag_st' },
        { id: 78, description: 'Bullish Intensity Medium-Term', field: 'bullish_intensity_mt' },
        { id: 79, description: 'Bullish Accumulation Medium-Term', field: 'bullish_accumulation_mt' },
        { id: 80, description: 'Bullish Accumulation Flag Medium-Term', field: 'bullish_accum_flag_mt' },
        { id: 81, description: 'Bullish Intensity Long-Term', field: 'bullish_intensity_lt' },
        { id: 82, description: 'Bullish Accumulation Long-Term', field: 'bullish_accumulation_lt' },
        { id: 83, description: 'Bullish Accumulation Flag Long-Term', field: 'bullish_accum_flag_lt' },
        { id: 84, description: 'IV Spike Flag Long-Term', field: 'iv_spike_flag_lt' },
        { id: 85, description: 'IV Spike Flag Medium-Term', field: 'iv_spike_flag_mt' },
        { id: 86, description: 'IV Spike Flag Short-Term', field: 'iv_spike_flag_st' },
        { id: 87, description: 'Smart Money Flag Long-Term', field: 'smart_money_flag_lt' },
        { id: 88, description: 'Smart Money Flag Medium-Term', field: 'smart_money_flag_mt' },
        { id: 89, description: 'Smart Money Flag Short-Term', field: 'smart_money_flag_st' },

       /* { id: 1, description: 'Net DELTA Short-Term', field: 'net_delta_st' },
        { id: 2, description: 'Net Gamma Short-Term', field: 'net_gamma_st' },
        { id: 3, description: 'Net Vega Short-Term', field: 'net_vega_st' },
        { id: 4, description: 'Out-of-the-money Bias Short-Term', field: 'otm_bias_st' },
        { id: 5, description: 'Implied Volatility Difference Short-Term', field: 'iv_diff_st' },
        { id: 6, description: 'Call/Put Open Interest Ratio Short-Term', field: 'cp_oi_ratio_st' },
        { id: 7, description: 'Call/Put Volume Ratio Short-Term', field: 'cp_volume_ratio_st' },
        {
          id: 8,
          description: 'Out-of-the-money Put Volume Short-Term',
          field: 'otm_put_volume_st'
        },
        {
          id: 9,
          description: 'Out-of-the-money Call Volume Short-Term',
          field: 'otm_call_volume_st'
        },
        {
          id: 10,
          description: 'Put Implied Volatility Volume Weighted Short-Term',
          field: 'put_iv_vw_st'
        },
        {
          id: 11,
          description: 'Call Implied Volatility Volume Weighted Short-Term',
          field: 'call_iv_vw_st'
        },
        { id: 12, description: 'Put Open Interest Short-Term', field: 'put_oi_st' },
        { id: 13, description: 'Call Open Interest Short-Term', field: 'call_oi_st' },
        { id: 14, description: 'Put Volume Short-Term', field: 'put_volume_st' },
        { id: 15, description: 'Call Volume Short-Term', field: 'call_volume_st' },
        {
          id: 16,
          description: 'Implied Volatility Difference 1 Day Short-Term',
          field: 'iv_diff_1d_st'
        },
        {
          id: 17,
          description: 'Implied Volatility Difference 2 Days Short-Term',
          field: 'iv_diff_2d_st'
        },
        {
          id: 18,
          description: 'Implied Volatility Difference 3 Days Short-Term',
          field: 'iv_diff_3d_st'
        },
        {
          id: 19,
          description: 'Implied Volatility Difference 4 Days Short-Term',
          field: 'iv_diff_4d_st'
        },
        {
          id: 20,
          description: 'Implied Volatility Difference 5 Days Short-Term',
          field: 'iv_diff_5d_st'
        },
        {
          id: 21,
          description: 'Implied Volatility Difference 6 Days Short-Term',
          field: 'iv_diff_6d_st'
        },
        {
          id: 22,
          description: 'Implied Volatility Difference 7 Days Short-Term',
          field: 'iv_diff_7d_st'
        },
        {
          id: 23,
          description: 'Implied Volatility Difference 8 Days Short-Term',
          field: 'iv_diff_8d_st'
        },
        {
          id: 24,
          description: 'Implied Volatility Difference 9 Days Short-Term',
          field: 'iv_diff_9d_st'
        },
        {
          id: 25,
          description: 'Implied Volatility Difference 10 Days Short-Term',
          field: 'iv_diff_10d_st'
        },
        {
          id: 26,
          description: 'Implied Volatility Difference 15 Days Short-Term',
          field: 'iv_diff_15d_st'
        },
        {
          id: 27,
          description: 'Implied Volatility Difference 30 Days Short-Term',
          field: 'iv_diff_30d_st'
        },
        {
          id: 28,
          description: 'Implied Volatility Difference 60 Days Short-Term',
          field: 'iv_diff_60d_st'
        },
        {
          id: 29,
          description: 'Implied Volatility Difference 120 Days Short-Term',
          field: 'iv_diff_120d_st'
        },

        { id: 34, description: 'Moneyness Difference Short-Term', field: 'moneyness_diff_st' },
        { id: 35, description: 'Moneyness Difference Long-Term', field: 'moneyness_diff_lt' },
        { id: 36, description: 'Net Theta Short-Term', field: 'net_theta_st' },
        { id: 37, description: 'Call Theta Short-Term', field: 'call_theta_st' },
        { id: 38, description: 'Put Theta Short-Term', field: 'put_theta_st' },
        { id: 39, description: 'Net Theta Long-Term', field: 'net_theta_lt' },
        { id: 40, description: 'Call Theta Long-Term', field: 'call_theta_lt' },
        { id: 41, description: 'Put Theta Long-Term', field: 'put_theta_lt' },
        { id: 42, description: 'Net Delta Long-Term', field: 'net_delta_lt' },
        { id: 43, description: 'Net Gamma Long-Term', field: 'net_gamma_lt' },
        { id: 44, description: 'Net Vega Long-Term', field: 'net_vega_lt' },
        { id: 45, description: 'Out-of-the-money Bias Long-Term', field: 'otm_bias_lt' },
        { id: 46, description: 'Implied Volatility Difference Long-Term', field: 'iv_diff_lt' },
        { id: 47, description: 'Call/Put Open Interest Ratio Long-Term', field: 'cp_oi_ratio_lt' },
        { id: 48, description: 'Call/Put Volume Ratio Long-Term', field: 'cp_volume_ratio_lt' },
        {
          id: 49,
          description: 'Out-of-the-money Put Volume Long-Term',
          field: 'otm_put_volume_lt'
        },
        {
          id: 50,
          description: 'Out-of-the-money Call Volume Long-Term',
          field: 'otm_call_volume_lt'
        },
        {
          id: 51,
          description: 'Put Implied Volatility Volume Weighted Long-Term',
          field: 'put_iv_vw_lt'
        },
        {
          id: 52,
          description: 'Call Implied Volatility Volume Weighted Long-Term',
          field: 'call_iv_vw_lt'
        },
        { id: 53, description: 'Put Open Interest Long-Term', field: 'put_oi_lt' },
        { id: 54, description: 'Put Volume Long-Term', field: 'put_volume_lt' },
        { id: 55, description: 'Call Open Interest Long-Term', field: 'call_oi_lt' },
        { id: 56, description: 'Call Volume Long-Term', field: 'call_volume_lt' },
        {
          id: 57,
          description: 'Implied Volatility Difference 1 Day Long-Term',
          field: 'iv_diff_1d_lt'
        },
        {
          id: 58,
          description: 'Implied Volatility Difference 2 Days Long-Term',
          field: 'iv_diff_2d_lt'
        },
        {
          id: 59,
          description: 'Implied Volatility Difference 3 Days Long-Term',
          field: 'iv_diff_3d_lt'
        },
        {
          id: 60,
          description: 'Implied Volatility Difference 4 Days Long-Term',
          field: 'iv_diff_4d_lt'
        },
        {
          id: 61,
          description: 'Implied Volatility Difference 5 Days Long-Term',
          field: 'iv_diff_5d_lt'
        },
        {
          id: 62,
          description: 'Implied Volatility Difference 6 Days Long-Term',
          field: 'iv_diff_6d_lt'
        },
        {
          id: 63,
          description: 'Implied Volatility Difference 7 Days Long-Term',
          field: 'iv_diff_7d_lt'
        },
        {
          id: 64,
          description: 'Implied Volatility Difference 8 Days Long-Term',
          field: 'iv_diff_8d_lt'
        },
        {
          id: 65,
          description: 'Implied Volatility Difference 9 Days Long-Term',
          field: 'iv_diff_9d_lt'
        },
        {
          id: 66,
          description: 'Implied Volatility Difference 10 Days Long-Term',
          field: 'iv_diff_10d_lt'
        },
        {
          id: 67,
          description: 'Implied Volatility Difference 15 Days Long-Term',
          field: 'iv_diff_15d_lt'
        },
        {
          id: 68,
          description: 'Implied Volatility Difference 30 Days Long-Term',
          field: 'iv_diff_30d_lt'
        },
        {
          id: 69,
          description: 'Implied Volatility Difference 60 Days Long-Term',
          field: 'iv_diff_60d_lt'
        },
        {
          id: 70,
          description: 'Implied Volatility Difference 120 Days Long-Term',
          field: 'iv_diff_120d_lt'
        },
        { id: 71, description: 'Open Interest Flow Short-Term', field: 'oi_flow_st' },
        { id: 72, description: 'Open Interest Flow Long-Term', field: 'oi_flow_lt' }*/
      ],
      editedTa: {
        enabled: true,
        target: 1,
        threshold: 50,
        action: 1,
        rule_type: 'options',
      },
      defaultTa: {
        enabled: true,
        target: 1,
        threshold: 50,
        action: 1,
        rule_type: 'options',
      }
    }
  },

  computed: {
    ...mapFields({
      appcriteria: 'app.whaleTraderCriteria',
      criteria: 'app.whaleTraderCriteria.signals.trend_signal',
      enabled: 'app.whaleTraderCriteria.signals.trend_signal.enabled',
      signalView: 'app.signalView',
      exit_time_periods: 'app.exit_time_periods'
    })
  },

  watch: {},

  mounted() {
    if (!this.rule.config || !this.rule.config.rule_type || this.rule.trend != 'options') {
      this.$emit('update:rule', { ...this.rule, config: Object.assign({}, this.defaultTa) })
    }
  },

  methods: {
    updateConfig(key, value) {
      this.$emit('update:rule', { ...this.rule, config: { ...this.rule.config, [key]: value } })
    }
  }
}
</script>
